import React from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import clsx from 'clsx';
import { Button } from 'components/ui';
import { REGULAR_DEADLINE } from 'constants/dates.constants';
import ackSclaPrefill from 'features/scla/api/ackSclaPrefill';
import { SCLA_APPLICATION_SECTIONS } from 'features/scla/config/sections';
import { SclaApplicationOutletContext } from 'features/scla/routes/SclaAppLayout';
import { sclaMissingFields, sclaStepState, type SclaStepId } from 'features/scla/utils/sectionStatus';
import formatSclaDate from '../home/formatSclaDate';
import styles from './SclaStep.module.scss';

interface ISclaStepCardProps {
  title: string;
  intro: string;
  children: React.ReactNode;
  backTo?: string;
  onContinue: () => Promise<boolean>;
  continueLabel: string;
  continueDisabled?: boolean;
  continueTitle?: string;
  readOnly?: boolean;
}

export default function SclaStepCard({
  title,
  intro,
  children,
  backTo,
  onContinue,
  continueLabel,
  continueDisabled = false,
  continueTitle,
  readOnly = false,
}: ISclaStepCardProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { application, setApplication } = useOutletContext<SclaApplicationOutletContext>();
  const [pending, setPending] = React.useState(false);
  const busy = React.useRef(false);
  const section = SCLA_APPLICATION_SECTIONS.find(item => pathname.endsWith(`/${item.id}`));
  const missing = section && sclaStepState(application, section.id as SclaStepId) === 'started'
    ? sclaMissingFields(application, section.id as SclaStepId)
    : [];
  const submitted = Boolean(application.submissionTimestamp)
    || application.appStatus === 'complete';
  const submittedOn = formatSclaDate(application.submissionTimestamp);
  const submittedWhen = submittedOn ? ` on ${submittedOn}` : '';
  const transcripts = pathname.endsWith('/transcripts');
  const prefillSections = application.profilePrefillSections || [];
  const showPrefillNote = Boolean(section && prefillSections.includes(section.id));
  const ackedPrefill = React.useRef(false);

  React.useEffect(() => {
    if (!section || !showPrefillNote || ackedPrefill.current || readOnly) return;
    ackedPrefill.current = true;
    ackSclaPrefill(application._id, section.id)
      .then(updated => setApplication(updated))
      .catch(() => {
        ackedPrefill.current = false;
      });
  }, [application._id, readOnly, section, setApplication, showPrefillNote]);

  const handleBack = () => {
    if (backTo) navigate(backTo);
  };

  const handleContinue = async () => {
    if (busy.current || continueDisabled) return;
    busy.current = true;
    setPending(true);
    try {
      await onContinue();
    } finally {
      busy.current = false;
      setPending(false);
    }
  };

  let buttonLabel = continueLabel;
  if (pending && continueLabel === 'Submit application') {
    buttonLabel = 'Submitting…';
  }

  let banner = '';
  if (submitted && transcripts) {
    banner = `Your application was submitted${submittedWhen}. You can still add or replace transcripts until ${REGULAR_DEADLINE}.`;
  } else if (submitted) {
    banner = `Your application was submitted${submittedWhen}, so this section can't be changed. Questions? Email info@urbantxt.com.`;
  }

  return (
    <section className={clsx(styles.card, readOnly && styles.cardLocked)}>
      {banner ? <p className={styles.submittedBanner}>{banner}</p> : null}
      {showPrefillNote ? (
        <p className={styles.prefillNote}>
          We filled in what we already had from your profile. Check it and update anything that changed.
        </p>
      ) : null}
      <h2>{title}</h2>
      <p className={styles.intro}>{intro}</p>
      {missing.length ? (
        <div className={styles.missing} role="status">
          <p>Missing:</p>
          <ul>
            {missing.map(item => (
              <li key={item.fieldId}>
                <a href={`#${item.fieldId}`}>{item.label}</a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {children}
      <div className={styles.footer}>
        {backTo ? (
          <Button variant="ghost" type="button" lockExempt onClick={handleBack}>
            ← Back
          </Button>
        ) : (
          <span />
        )}
        {readOnly ? null : (
          <Button
            type="button"
            onClick={handleContinue}
            disabled={continueDisabled || pending}
            title={continueTitle}
            loading={pending}
          >
            {buttonLabel}
          </Button>
        )}
      </div>
    </section>
  );
}
